import { useCallback } from "react";
import { useHubSpotAdapterAxiosInstance } from "./useHubSpotAdapterAxiosInstance.ts";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";

export type StatsFiguresDTO = {
  callsDone: number;
  wonDeals: number;
  cr: number;
  noShow: number;
  toReschedule: number;
  booked: number;
  bookedWeb: number;
  bookedSetter: number;
  cancelled: number;
  pending: number;
  reschedules: number;
  capacityDays: number;
  capacityTotal: number;
  callsOnCapacityDays: number;
  daysAtCap: number;
  callsOverCap: number;
};

export type WeekStatsDTO = StatsFiguresDTO & { weekStart: string };

export type CloserStatsDTO = {
  closerId: string;
  name: string;
  email: string | null;
  exclusionReason: string | null;
  acceptsWeb: boolean;
  acceptsSetter: boolean;
  tierRank: number | null;
  currentCr: number | null;
  currentCrWonDeals: number | null;
  currentCrCallsDone: number | null;
  inTrial: boolean;
  capacity: number | null;
  totals: StatsFiguresDTO;
  weeks: WeekStatsDTO[];
};

export type CloserStatsReportDTO = {
  fromDay: string;
  dataFromDay: string;
  toDay: string;
  capacityDefault: number | null;
  crWindowDays: number;
  totals: StatsFiguresDTO;
  weeks: WeekStatsDTO[];
  closers: CloserStatsDTO[];
};

export function useGetSchedulerCloserStats() {
  const axiosInstance = useHubSpotAdapterAxiosInstance();
  const { withLoading, loading } = useWithLoading<CloserStatsReportDTO>();

  const getStatsCall = useCallback(
    async ({ fromDay, toDay }: { fromDay: string; toDay: string }) => {
      const { data } = await axiosInstance.get<CloserStatsReportDTO>(
        `/closer-availability/scheduler/stats?fromDay=${encodeURIComponent(fromDay)}&toDay=${encodeURIComponent(toDay)}`,
      );

      if (!Array.isArray(data?.closers) || !Array.isArray(data?.weeks)) {
        console.error("Unexpected closer stats response:", data);
        throw new Error("Unexpected response format from HubSpot adapter");
      }

      return data;
    },
    [axiosInstance],
  );

  const getSchedulerCloserStats = useCallback(
    async (range: { fromDay: string; toDay: string }) => {
      return await withLoading(() => getStatsCall(range));
    },
    [withLoading, getStatsCall],
  );

  return {
    getSchedulerCloserStats,
    loading,
  };
}
